import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';

const normalize = (value) => (value ?? '').trim();

/**
 * Decide whether a UW's edit to the Details "Policy restrictions" and/or "Change of Coverage"
 * sections should be forwarded to policy-mod as `uwReviewAnswers` when confirming a farm endorsement
 * (#32405; Change of Coverage added #34275).
 *
 * Both are form-only fields (not part of the segment payload). When a UW changes either we forward
 * it so policy-mod stores it on the version-history entry and mirrors it onto the newest segment —
 * which rolls the FMI 122 restriction form (restrictions) and drives the Change of Coverage notice
 * (changeOfCoverage) on the endorsement documents.
 *
 * Returns the merged payload only when ALL hold, else undefined:
 *  - farm-based policy (Home / Rental / Farm),
 *  - the confirming user is an underwriter (`canApproveUWReview`),
 *  - at least one of the two fields actually changed (whitespace-insensitive; "" ⇒ removal),
 *  - the change is NOT already flowing through the FarmUWQuestionsModal (`isFarmUWReview`), which
 *    forwards these fields itself.
 *
 * Only the changed field(s) are included, each preserving the UW's raw text (only the change check
 * is whitespace-insensitive).
 */
export const buildRestrictionUwReviewAnswers = ({
  policyType,
  canApproveUWReview,
  isFarmUWReview,
  currentRestriction,
  editedRestriction,
  currentChangeOfCoverage,
  editedChangeOfCoverage
}) => {
  if (!isFarmBasedPolicyType(policyType) || !canApproveUWReview || isFarmUWReview) {
    return undefined;
  }
  const answers = {};
  // `undefined` means the field was never part of this edit session (e.g. a preview reopened from a
  // link that didn't flow through the Details form). Only a real edit seeds it to a string ("" for a
  // deliberate removal), so never treat `undefined` as a change — otherwise a reopened-preview
  // confirm would silently clear an existing value.
  if (editedRestriction !== undefined && normalize(editedRestriction) !== normalize(currentRestriction)) {
    answers.restrictions = editedRestriction;
  }
  if (
    editedChangeOfCoverage !== undefined &&
    normalize(editedChangeOfCoverage) !== normalize(currentChangeOfCoverage)
  ) {
    answers.changeOfCoverage = editedChangeOfCoverage;
  }
  return Object.keys(answers).length > 0 ? answers : undefined;
};

/**
 * Capture a UW's edited restriction + change-of-coverage against the preview they were generated for
 * (#32405; #34275).
 *
 * Both are form-only fields, so they never ride on the segment/preview payload — they have to be
 * carried separately and applied on confirm. We tag the captured values with the preview's id so the
 * resolvers apply them ONLY when that exact preview is confirmed. Keying by previewId (rather than a
 * bare value) is what lets the capture survive the preview→confirm round-trip without a shared ref
 * being clobbered when the preview store updates — the reopened-#32405 bug, where a cleared value
 * never reached policy-mod (`uwReviewAnswers` arrived as null).
 */
export const captureEditedRestriction = (previewId, restrictions, changeOfCoverage) => ({
  previewId,
  restrictions,
  changeOfCoverage
});

/**
 * Resolve the captured restriction for the preview being confirmed. Returns the captured value
 * ("" for a deliberate removal) only when the capture belongs to `previewId`; otherwise undefined,
 * so a capture from an earlier in-session edit or a link-loaded preview is never forwarded.
 */
export const resolveEditedRestriction = (capture, previewId) =>
  capture && capture.previewId === previewId ? capture.restrictions : undefined;

/**
 * Resolve the captured change-of-coverage for the preview being confirmed (#34275). Same
 * preview-scoped semantics as resolveEditedRestriction.
 */
export const resolveEditedChangeOfCoverage = (capture, previewId) =>
  capture && capture.previewId === previewId ? capture.changeOfCoverage : undefined;

export default buildRestrictionUwReviewAnswers;
